import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, MapPin, CheckCircle2, AlertTriangle } from 'lucide-react';
import { forwardGeocode, reverseGeocode } from '../lib/geolocation';

const EMPTY = {
  name: '', email: '', phone: '', isLoginEnabled: true,
  address: '',          // dirección legible (UI)
  geoCoordinates: '',   // 'lat,lng' interno para el backend
  maxGeoDeviation: 100,  // metros
};

const WorkplaceModal = ({ open, initial, onClose, onSubmit }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [resolvingAddress, setResolvingAddress] = useState(false);
  const [geocodeStatus, setGeocodeStatus] = useState(null); // 'ok' | 'not-found' | 'error'
  const lastResolvedRef = useRef('');

  useEffect(() => {
    if (!open) return;
    const initialForm = initial ? {
      name: initial.name || '',
      email: initial.email || '',
      phone: initial.phone || '',
      isLoginEnabled: initial.isLoginEnabled ?? true,
      address: '',
      geoCoordinates: initial.geoCoordinates || '',
      maxGeoDeviation: typeof initial.maxGeoDeviation === 'number' ? initial.maxGeoDeviation : 100,
    } : EMPTY;
    setForm(initialForm);
    setApiError(null);
    setGeocodeStatus(initial?.geoCoordinates ? 'ok' : null);
    lastResolvedRef.current = '';

    // Si la sede ya tenía coordenadas, intenta mostrar la calle (reverse).
    if (initial?.geoCoordinates) {
      reverseGeocode(initial.geoCoordinates).then((label) => {
        if (label) setForm((f) => ({ ...f, address: label }));
      }).catch(() => {});
    }
  }, [open, initial]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));

  // Resuelve la dirección actual a coords. Si la dirección no cambió desde la
  // última resolución, no reconsulta.
  const resolveAddress = async () => {
    const q = form.address.trim();
    if (!q) {
      setGeocodeStatus(null);
      return;
    }
    if (q === lastResolvedRef.current && form.geoCoordinates) {
      setGeocodeStatus('ok');
      return;
    }
    setResolvingAddress(true);
    setGeocodeStatus(null);
    try {
      const results = await forwardGeocode(q);
      if (!results || results.length === 0) {
        setGeocodeStatus('not-found');
        setForm((f) => ({ ...f, geoCoordinates: '' }));
        return;
      }
      const top = results[0];
      lastResolvedRef.current = q;
      setForm((f) => ({ ...f, geoCoordinates: top.coords, address: top.label || q }));
      setGeocodeStatus('ok');
    } catch {
      setGeocodeStatus('error');
    } finally {
      setResolvingAddress(false);
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim()) { setApiError({ message: 'El nombre es obligatorio.' }); return; }
    // Si el user introdujo dirección pero no la resolvió todavía, intentar resolver antes de guardar.
    let coords = form.geoCoordinates;
    if (form.address.trim() && !coords) {
      await resolveAddress();
      coords = form.geoCoordinates;
    }
    setSubmitting(true);
    setApiError(null);
    try {
      const radius = Number(form.maxGeoDeviation);
      await onSubmit({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        isLoginEnabled: form.isLoginEnabled,
        // Solo incluimos coords si las tenemos; si no, va string vacío (backend → null).
        geoCoordinates: coords || null,
        maxGeoDeviation: Number.isFinite(radius) && radius > 0 ? radius : 100,
      });
      onClose();
    } catch (err) {
      setApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">{isEdit ? 'Editar' : 'Nueva'}</div>
            <h2 className="font-display text-lg font-bold text-ink">{isEdit ? 'Editar sede' : 'Nueva sede'}</h2>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Nombre</label>
            <input type="text" value={form.name} onChange={set('name')}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
              autoFocus />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Email</label>
            <input type="email" value={form.email} onChange={set('email')}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Teléfono</label>
            <input type="tel" value={form.phone} onChange={set('phone')}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
              placeholder="+34 600 000 000" />
          </div>

          <div className="border-t border-slate-100 pt-4">
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">
              Dirección de fichaje
            </label>
            <div className="flex items-stretch gap-2">
              <div className="relative flex-1">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => { set('address')(e); setGeocodeStatus(null); }}
                  onBlur={resolveAddress}
                  placeholder="Calle, número, ciudad…"
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                />
              </div>
              <button
                type="button"
                onClick={resolveAddress}
                disabled={resolvingAddress || !form.address.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-primary-darker border border-primary/30 hover:bg-primary-soft transition-colors disabled:opacity-50"
                title="Resolver coordenadas a partir de la dirección"
              >
                {resolvingAddress ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} />}
                Resolver
              </button>
            </div>
            <div className="text-[11px] mt-1.5">
              {geocodeStatus === 'ok' && form.geoCoordinates && (
                <span className="inline-flex items-center gap-1 text-success">
                  <CheckCircle2 size={11} /> Coordenadas guardadas para la validación de fichaje.
                </span>
              )}
              {geocodeStatus === 'not-found' && (
                <span className="inline-flex items-center gap-1 text-warning">
                  <AlertTriangle size={11} /> No se encontró esa dirección. Prueba a afinar (calle, ciudad, código postal).
                </span>
              )}
              {geocodeStatus === 'error' && (
                <span className="inline-flex items-center gap-1 text-danger">
                  <AlertTriangle size={11} /> Error al consultar el geocodificador. Reintenta.
                </span>
              )}
              {!geocodeStatus && (
                <span className="text-ink-muted">
                  Cuando un trabajador con geolocalización fiche, se validará que esté dentro del radio.
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">
              Radio máximo permitido
              <span className="text-ink-muted/60 normal-case ml-1">(metros)</span>
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={form.maxGeoDeviation}
              onChange={set('maxGeoDeviation')}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
            />
            <div className="text-[11px] text-ink-muted mt-1">
              Distancia máxima desde la dirección configurada para considerar el fichaje válido. Por defecto 100 m.
            </div>
          </div>

          <label className="flex items-center gap-3 pt-2 cursor-pointer">
            <input type="checkbox" checked={form.isLoginEnabled} onChange={toggle('isLoginEnabled')}
              className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary" />
            <div>
              <div className="text-sm font-semibold text-ink">Login habilitado</div>
              <div className="text-xs text-ink-muted">Si se desactiva, los trabajadores de esta sede no podrán acceder al sistema.</div>
            </div>
          </label>

          {apiError && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2">{apiError.message}</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60">
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? 'Guardar cambios' : 'Crear sede'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkplaceModal;
